// app/src/features/branch/useBranchWiring.js
import { useCallback } from "react";
import { useActiveForecast } from "../../hooks/useActiveForecast.js";
import { useBranchEngineConfirm } from "../../hooks/useBranchEngineConfirm.js";
import { useBranchForecast } from "../../hooks/useBranchForecast.js";
import { useCustomEventPopover } from "../../hooks/useCustomEventPopover.js";
import { useSession } from "../../hooks/useSession.js";
import { apiPost } from "../../lib/apiClient.js";
import { premiseCleared } from "../../lib/forecast/reducer.js";
import { recordUiEvent } from "../../lib/logRingBuffer";
import { beginIntent } from "../../lib/telemetry/instrument.js";

/**
 * The branch wiring: the deep-engine confirm gate (`useBranchEngineConfirm`), the branch command
 * adapters (`useBranchForecast`, its 20-field argument object passed unchanged — "hook args
 * unchanged"), the custom-event popover's handlers (`useCustomEventPopover`, as a surface of the
 * forecast it was opened on) and the settled-premise clear (`clearBranchPremise`). Called where
 * `AppInner` called `useBranchForecast`: after the hotkeys, before the archive / collapse
 * callbacks, which stay in `AppInner`.
 *
 * The popover's state (`customEvtPopoverRunId`, `customEvtPopover`) is NOT here: it is derived
 * from `runState` early in `AppInner` and read by the chat composer before this point, so it
 * arrives as an input and reaches `CustomEventPopover` through the frame context. The store
 * reaches this hook as `getRunState` / `dispatchRun` (injected, never imported — C-23); the tour
 * globals as the getter / setter thunks `AppInner` builds (threaded, never copied).
 * `forecastLiveProgressEnabled`, `guardForTos` and `persistenceEnabled` are read from the session
 * context through `useSession()`; the active forecast through `useActiveForecast()`.
 *
 * @param {object} deps
 * @param {Function} deps.getRunState  the run store's live reader (injected, never imported — C-23)
 * @param {Function} deps.dispatchRun  the run store's dispatcher (injected, never imported — C-23)
 * @param {Function} deps.getTourFixture  `() => __tourFixture` (AppInner's module global)
 * @param {Function} deps.getAuthEnabled  `() => __authEnabled`
 * @param {Function} deps.getTourBranchIncrease  `() => __tourBranchIncrease`
 * @param {Function} deps.setTourBranchIncrease  `(v) => __setTourBranchIncrease(v)`
 * @param {string|null} deps.customEvtPopoverRunId  the run whose popover is on screen (AppInner's)
 * @param {object|null} deps.customEvtPopover  that run's popover slot, or null (AppInner's)
 */
export function useBranchWiring({
  // useBranchForecast's argument object (20 fields; the confirm gate's `confirmBranchEngine` is
  // this hook's own)
  compareRoots,
  forecastEngine,
  series,
  setLoading,
  setPendingPreprocess,
  pendingAbortsRef,
  slideCache,
  eventAnchorMs,
  startBranchAnim,
  setLastObs,
  getTourFixture,
  getAuthEnabled,
  getTourBranchIncrease,
  setTourBranchIncrease,
  mockToday,
  // useCustomEventPopover's
  customEvtPopoverRunId,
  customEvtPopover,
  // clearBranchPremise's
  getRunState,
  dispatchRun,
}) {
  const { forecastLiveProgressEnabled, guardForTos, persistenceEnabled } = useSession();
  const active = useActiveForecast();
  // useBranchForecast holds the clicked-row/DOM adapters and dispatch helpers, which are React-only
  // and cannot be store commands. Tour globals are threaded as getter/setter thunks, never copied.
  // The confirm surface useBranchForecast awaits before a branch inherits a deep-lane engine the
  // picker did not select keeps its state in the hook; the modal that renders `branchEngineConfirm`
  // is BranchControls.jsx, beside the delete-forecast confirm as it was.
  const { branchEngineConfirm, confirmBranchEngine, settleBranchEngineConfirm } =
    useBranchEngineConfirm();
  const branchForecast = useBranchForecast({
    active,
    compareRoots,
    forecastEngine,
    forecastLiveProgressEnabled,
    guardForTos,
    confirmBranchEngine,
    persistenceEnabled,
    series,
    setLoading,
    setPendingPreprocess,
    pendingAbortsRef,
    slideCache,
    eventAnchorMs,
    startBranchAnim,
    setLastObs,
    getTourFixture,
    getAuthEnabled,
    getTourBranchIncrease,
    setTourBranchIncrease,
    // Typed-trigger branches preprocess their text into a contract mid-flight; mockToday
    // keeps that as-of date on the debug clock.
    mockToday,
  });
  const { handleBranch, handleCustomEventBranch, rerunCustomEventBranch, prefetchDerivedBranch } =
    branchForecast;

  // A typed trigger goes STRAIGHT to the branch path, which preprocesses it into a real
  // contract (question + resolution criteria + close date) and then shows the branch's own
  // question — "If X happens, will Y?" — for confirmation. The popover is a surface of the
  // forecast it was opened on (useCustomEventPopover).
  const {
    descriptionRef: customEvtDescriptionRef,
    dateRef: customEvtDateRef,
    openCustomEventPopover,
    closeCustomEventPopover,
    submitCustomTrigger,
    chatCustomEventBranch,
  } = useCustomEventPopover({
    active,
    handleCustomEventBranch,
    popoverRunId: customEvtPopoverRunId,
    popoverOpen: !!customEvtPopover,
  });

  // Acknowledge a settled branch premise and hide its lane. ARCHIVES — the row is kept (the
  // conditional forecast stays scoreable, and a rewind to an earlier vintage can still render
  // it). Resolves `{ok, status}` so the panel can say why a refused clear did nothing.
  const clearBranchPremise = useCallback(
    async (branchId) => {
      if (!branchId) return { ok: false, status: null };
      // A branch of a forecast shared WITH the viewer (owner-leg checked first, as the reducer
      // does) goes by its pg UUID to the collaborator route: the owner route looks the row up
      // by (client_id, viewer) and 404s a branch someone else created.
      const st = getRunState();
      const shared = st.runsById?.[branchId] == null ? st.sharedRunsById?.[branchId] : null;
      if (shared && !shared.forecastId) return { ok: false, status: 404 };
      const intent = beginIntent(
        "branch.clear",
        { forecast_id: branchId, kind: "premise" },
        { origin: "branch-terminal.clear", surface: "timeline.branch-terminal" },
      );
      const res = await apiPost(
        shared
          ? `/api/forecasts/${encodeURIComponent(shared.forecastId)}/branch/premise/clear`
          : `/api/forecasts/${encodeURIComponent(branchId)}/premise/clear`,
        undefined,
        { intentId: intent.id || null },
      );
      intent.end(res && res.ok ? "ok" : "error");
      if (!res || !res.ok) {
        // The lane is left alone — the server is the authority on whether it is clearable.
        console.warn("[premise] clear rejected", res && res.status);
        return { ok: false, status: res ? res.status : null };
      }
      // Reflect it immediately rather than waiting for a refetch: the store's copy of the
      // fork carries the premise the visible-branch selector reads.
      dispatchRun(premiseCleared(branchId, res.body?.premise ?? null));
      recordUiEvent({ event: "ui.branch.premise_cleared", branch_id: branchId });
      return { ok: true, status: res.status };
    },
    // getRunState / dispatchRun are the store's handles (stable hook inputs), listed so the
    // compiler's inferred deps match.
    [getRunState, dispatchRun],
  );

  return {
    branchEngineConfirm,
    settleBranchEngineConfirm,
    handleBranch,
    rerunCustomEventBranch,
    prefetchDerivedBranch,
    customEvtDescriptionRef,
    customEvtDateRef,
    openCustomEventPopover,
    closeCustomEventPopover,
    submitCustomTrigger,
    chatCustomEventBranch,
    clearBranchPremise,
  };
}
